/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies, import/no-unresolved
import HeatMapGrid from 'react-heatmap-grid';
import 'moment-timezone';
import { DeviceType, PlotDataType, PriorityList, TimeSpan } from '../constants/telosair-interfaces';
import {
  generateBooleanArray,
  getFixedNumberForGraph,
  getFormattedTooltipLabel,
  getFormattedXAxisLabel,
  getParameterByName
} from '../_helpers/globalHelper';
import { useData } from '../context/data.context';
import { getPriorityColor } from '../_helpers/dashboardHelper';
import Header from './Header';
import Loader from './loader';
import { COLOR_BY_PRIORITY } from '../constants/global-constants';

type Props = {
  data: PlotDataType[];
  timeSpan: TimeSpan;
  extendedLabel: string;
  isDataLoading: boolean;
  type: DeviceType;
};
/**
 * React component for Dashboard heatmap.
 * @param props - Properties.
 * @returns - Component.
 */
export default function DashboardHeatmapWidget(props: Props): React.ReactElement {
  const { data, timeSpan, isDataLoading, extendedLabel, type } = props;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { userConfigData, parameters, clientThresholdSettings, userConfigDataV2 } = useData();
  const [resized, setResized] = useState(true);
  const [loading, setLoading] = useState(true);
  const [scrollContainerData, setScrollContainerData] = useState<{
    loaded: boolean;
    height: null | number;
    width: null | number;
  }>({ loaded: false, height: null, width: null });
  const [mapData, setMapData] = useState<number[][]>([]);
  const xLabels = data.map((item) =>
    getFormattedXAxisLabel(item.timestamp, userConfigData, timeSpan)
  );
  const yLabels = parameters.map((item) => item.shortName);

  useEffect(() => {
    if (!isDataLoading) {
      setLoading(true);
      const tempData: number[][] = [];
      parameters.forEach((parameter) => {
        const tempParameterData: number[] = [];
        data.forEach((item) => {
          tempParameterData.push(
            getFixedNumberForGraph(
              parameter.parameterName,
              item.parameters[parameter.parameterName]
            )
          );
        });
        tempData.push(tempParameterData);
      });

      setMapData(tempData);
      setLoading(false);
    }
  }, [parameters, data, isDataLoading]);

  useEffect(() => {
    if (!loading && scrollContainerRef.current && resized) {
      const rect = scrollContainerRef.current?.getBoundingClientRect();
      setScrollContainerData({ height: rect.height, loaded: true, width: rect.width });
      setResized(false);
    }
  }, [resized, loading]);

  /**
   * Function to handle event resize.
   */
  const handleResize = (): void => {
    setResized(true);
    setScrollContainerData({ loaded: false, height: null, width: null });
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="flex flex-1 flex-col bg-container-500 rounded p-4 pt-2 gap-4">
      <div className="flex flex-1 relative" ref={scrollContainerRef}>
        {!scrollContainerData.loaded && <Loader />}
        {scrollContainerData.loaded && (
          <div
            style={{ height: scrollContainerData.height || 'auto' }}
            className="flex flex-1 overflow-y-auto ewlative"
          >
            <div className="flex flex-1">
              {!loading && mapData.length === yLabels.length && (
                <HeatMapGrid
                  yLabelWidth="3.2rem"
                  xLabels={xLabels}
                  yLabels={yLabels}
                  xLabelsLocation="top"
                  xLabelsVisibility={generateBooleanArray(xLabels.length)}
                  xLabelWidth={50}
                  data={mapData}
                  squares={false}
                  onClick={(x: number, y: number) => null}
                  cellStyle={(
                    background: string,
                    value: number,
                    min: number,
                    max: number,
                    dataList: number[][],
                    x: number,
                    y: number
                  ) => {
                    const parameter = getParameterByName(yLabels[y], parameters);
                    return {
                      background: parameter
                        ? COLOR_BY_PRIORITY[
                            getPriorityColor(
                              parameter.parameterName,
                              value,
                              clientThresholdSettings,
                              userConfigDataV2,
                              type
                            ) as PriorityList
                          ]
                        : '',
                      fontSize: '12px',
                      color: '#D0D5DD'
                    };
                  }}
                  cellRender={(value: number) => value}
                  title={(value: number, unit: string, xIndex: number) =>
                    `${getFormattedTooltipLabel(data[xIndex].timestamp, userConfigData)}: ${value}`
                  }
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
